import { useEffect, useMemo, useRef, Suspense } from 'react';
import PropTypes from 'prop-types';
import { useSubmit, useLoaderData, Await } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from 'client/components/aria/Button/Button';
import TextInput from 'client/components/GDS/TextInput/TextInput';
import WorkpackExportOptions from '../../components/WorkpackExportOptions/WorkpackExportOptions';
import WorkpackPrintPreview from '../../components/WorkpackPrintPreview/WorkpackPrintPreview';
import Loading from 'client/components/ui/Loading/Loading';
import { jsonToFormData } from 'client/utils/helpers/Workpack/Workpack';
import { CREATE_WORKPACK_PAGE, HSG47_PAGE } from 'client/utils/routes/routeConstants';
import defaultAriaProps from 'client/utils/constants/ariaPropTypes';
import useAuth from 'client/utils/auth/useAuth';
import { useSelector } from 'react-redux';
import WorkpackSummary from '../WorkpackSummary/WorkpackSummary';
import NavLink from 'client/components/aria/NavLink/NavLink';
import { electricitySymbologyFeatureFlag, printPreviewFeatureFlag } from 'client/utils/constants/frontEndConfig';
import GeoJSON from 'ol/format/GeoJSON';
import electricityOptOutImage from 'client/assets/imgs/electricityOptOut.png';
import electricityOptInImage from 'client/assets/imgs/electricityOptIn.png';
import OptIn from 'client/components/aria/OptIn/OptIn';
import styles from './WorkpackForm.module.scss';
import { COLOUR_CODING_ELECTRICITY_LEGEND_ITEMS, DEFAULT_ELECTRICITY_LEGEND_ITEMS } from './constants';

const WorkpackDetails = ({ children, className }) => (
    <h2 className={`govuk-heading-m ${className || ''}`}>{children}</h2>
);

const WorkpackForm = ({ enquiry, onChange, setErrors, showSummary, setShowSummary, handleEdit, handleChangePurpose, ...props }) => {
    const submit = useSubmit();
    const { user } = useAuth();
    const intl = useIntl();
    const { capabilities } = useLoaderData();
    const resolvedCapabilitiesRef = useRef(null);
    const featuresByAssetType = useSelector(state => state.assets.layers);
    const hasSubmittedRef = useRef(false);

    const aoi = useMemo(() => {
        if (enquiry?.geometry) {
            return new GeoJSON().readFeature({ geometry: enquiry.geometry, type: 'Feature' });
        }
        return null;
    }, [enquiry?.geometry]);

    const formik = useFormik({
        initialValues: {
            title: enquiry?.title || '',
            jobReference: enquiry?.jobReference || 'N/A',
            description: enquiry?.description || '',
            isEmergency: enquiry?.isEmergency || false,
            purpose: enquiry?.purpose || null,
            geometry: enquiry?.geometry || null,
            workpackOrientation: enquiry?.workpackOrientation || null,
            workpackScale: enquiry?.workpackScale || null,
            electricityStylingOptIn: enquiry?.electricityStylingOptIn === true || enquiry?.electricityStylingOptIn === 'true',
            userJourneyStartTime: enquiry?.userJourneyStartTime || '',
            userJourneyTransId: enquiry?.userJourneyTransId || ''
        },
        validationSchema: Yup.object({
            title: Yup
                .string()
                .trim()
                .max(255, intl.formatMessage({ id: 'workpackForm.name.validation.length' }, {
                    characterCount: 255
                }))
                .required(intl.formatMessage({ id: 'workpackForm.name.validation.required' })),
            geometry: Yup
                .object()
                .nullable()
                .required(intl.formatMessage({ id: 'workpackForm.geometry.required' })),
            workpackOrientation: Yup
                .string()
                .required(),
            workpackScale: Yup
                .string()
                .required(),
            electricityStylingOptIn: Yup
                .boolean()
                .required()
        }),
        onSubmit: values => {
            if (hasSubmittedRef.current) {
                return;
            }

            hasSubmittedRef.current = true;

            const formData = jsonToFormData({
                ...values
            });

            submit(formData, {
                action: CREATE_WORKPACK_PAGE,
                method: 'POST'
            });
        }
    });

    const handleChange = (name, value) => {
        formik.setFieldValue(name, value);
    };

    const handleOrientationChange = (orientation) => {
        formik.setFieldValue('workpackOrientation', orientation);

        if (resolvedCapabilitiesRef.current) {
            const layout = resolvedCapabilitiesRef.current.layouts.find(layout => layout.name === orientation);
            const scaleIsValidForOrientation = layout?.scales.find(s => s.toString() === formik.values.workpackScale);
            if (!scaleIsValidForOrientation) {
                handleChange('workpackScale', null);
            }
        }
    };

    const handleChangeFromEvent = (event) => {
        const { name, value } = event.target;
        formik.setFieldValue(name, value);
    };

    useEffect(() => {
        if (onChange) {
            formik.validateForm(formik.values);
        }
    }, [enquiry]);

    const scrollToTop = () => {
        const navigationElement = document.getElementById('navigation');
        if (navigationElement) {
            navigationElement.scrollIntoView();
        }
    };

    const handleShowSummary = () => {
        const touchedFields = Object.keys(formik.values).reduce((acc, key) => {
            acc[key] = true;
            return acc;
        }, {});

        formik.validateForm(formik.values).then(errors => {
            formik.setTouched(touchedFields);
            const validationErrors = [];

            Object.keys(formik.errors).forEach(field => {
                validationErrors.push({ id: field, message: formik.errors[field] });
            });

            if (Object.keys(errors).length > 0) {
                formik.setErrors(errors);
                setErrors(validationErrors);
                scrollToTop();
            } else {
                setShowSummary(true);
                setErrors([]);
                scrollToTop();
            }
        });
    };

    useEffect(() => {
        if (onChange) {
            // Merge formik values with existing enquiry data to preserve purpose, isEmergency, etc.
            onChange(formik.values);
        }
    }, [formik.values]);

    return (
        <form onSubmit={formik.handleSubmit}>
            {showSummary === false
                ? <>
                    <div>
                        {user?.organisationName && (
                            <div className="govuk-body govuk-!-margin-bottom-6">
                                <p className="govuk-body">
                                    <FormattedMessage
                                        id='createWorkpackPage.purpose.currentOrganisation'
                                        values={{ organisationName: user.organisationName }}
                                    />
                                </p>
                            </div>
                        )}
                        <WorkpackDetails>
                            {intl.formatMessage({ id: 'workpackForm.details.title' })}
                        </WorkpackDetails>
                        <div className="govuk-grid-row">
                            <div className="govuk-grid-column-two-thirds">
                                <TextInput
                                    label={intl.formatMessage({ id: 'workpackForm.name.label' })}
                                    description={intl.formatMessage({ id: 'workpackForm.name.description' })}
                                    id='title'
                                    name='title'
                                    autoComplete='off'
                                    onChange={handleChangeFromEvent}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.title}
                                    errorMessage={formik.touched.title && formik.errors.title ? formik.errors.title : null}
                                    data-testid='title'
                                />
                            </div>
                        </div>
                        <hr className="govuk-section-break govuk-section-break--l govuk-section-break--visible" />
                        <div className="govuk-grid-row">
                            <div className="govuk-grid-column-two-thirds govuk-!-margin-bottom-4">
                                <p className="govuk-body-m">
                                    {intl.formatMessage({ id: 'workpackForm.scaling.title' })}
                                </p>
                                <div className="govuk-hint">
                                    <p>{intl.formatMessage({ id: 'workpackForm.scaling.description' })}</p>
                                </div>
                            </div>
                        </div>

                        <div className="govuk-grid-row">
                            <div className="govuk-grid-column-full">
                                <Suspense fallback={
                                    <Loading
                                        variant="compact"
                                        message={intl.formatMessage({ id: 'workpackForm.exportOptions.loading' })}
                                        data-testid="export-options-loading"
                                    />
                                }>
                                    <Await resolve={capabilities}>
                                        {(resolvedCapabilities) => {
                                            resolvedCapabilitiesRef.current = resolvedCapabilities;
                                            return (
                                                <WorkpackExportOptions
                                                    scale={formik.values.workpackScale}
                                                    onScaleChange={val => handleChange('workpackScale', val)}
                                                    orientation={formik.values.workpackOrientation}
                                                    onOrientationChange={handleOrientationChange}
                                                    capabilities={resolvedCapabilities}
                                                    data-testid={props['data-testid'] ? props['data-testid'] + '.export' : null}
                                                />
                                            );
                                        }}
                                    </Await>
                                </Suspense>
                            </div>
                        </div>

                        {printPreviewFeatureFlag && aoi && (
                            <div className="govuk-grid-row">
                                <div className="govuk-grid-column-full">
                                    <WorkpackPrintPreview
                                        orientation={formik.values.workpackOrientation}
                                        scale={formik.values.workpackScale}
                                        aoi={aoi}
                                        featuresByAssetType={featuresByAssetType}
                                        onEditArea={handleEdit}
                                        data-testid={props['data-testid'] ? props['data-testid'] + '.printPreview' : null}
                                    />
                                </div>
                            </div>
                        )}

                        {electricitySymbologyFeatureFlag && <OptIn
                            data-testid='electricity-opt-in'
                        >
                            <OptIn.Header>
                                {intl.formatMessage({ id: 'workpackForm.electricityOptIn.title' })}
                            </OptIn.Header>
                            <OptIn.Description>
                                {intl.formatMessage({ id: 'workpackForm.electricityOptIn.description' })}
                            </OptIn.Description>
                            <div className="govuk-radios" data-module="govuk-radios">
                                <OptIn.Option
                                    id="electricity-opt-in-default"
                                    name="electricityOptIn"
                                    value='false'
                                    checked={formik.values.electricityStylingOptIn === false}
                                    onChange={(event) => handleChange('electricityStylingOptIn', event.target.value === 'true')}
                                >
                                    <span className={styles.optionLabelText}>
                                        {intl.formatMessage({ id: 'workpackForm.electricityOptIn.option.default' })}
                                    </span>
                                    <span className={styles.optionGrid}>
                                        <span className={styles.optionColumn}>
                                            <img
                                                className={styles.optionImage}
                                                src={electricityOptOutImage}
                                                alt={intl.formatMessage({ id: 'workpackForm.electricityOptIn.option.default.alt' })}
                                            />
                                        </span>
                                        <span className={styles.optionColumn}>
                                            <div className={styles.optionPlaceholder} data-testid="electricity-opt-in-default-placeholder">
                                                <h4 className={styles.optionLegendTitle}>
                                                    <FormattedMessage id='workpackForm.electricityOptIn.legend.title' />
                                                </h4>
                                                {DEFAULT_ELECTRICITY_LEGEND_ITEMS.map(({ colour, id }) => (
                                                    <div className={styles.optionLegendItem} key={id}>
                                                        <span
                                                            className={styles.optionSwatch}
                                                            style={{ backgroundColor: colour }}
                                                        />
                                                        <FormattedMessage id={`workpackForm.electricityOptIn.legend.${id}`} />
                                                    </div>
                                                ))}
                                            </div>
                                        </span>
                                    </span>
                                </OptIn.Option>
                                <OptIn.Option
                                    id="electricity-opt-in-colour-coding"
                                    name="electricityOptIn"
                                    value='true'
                                    checked={formik.values.electricityStylingOptIn === true}
                                    onChange={(event) => handleChange('electricityStylingOptIn', event.target.value === 'true')}
                                >
                                    <span className={styles.optionLabelText}>
                                        {intl.formatMessage({ id: 'workpackForm.electricityOptIn.option.colourCoding' })}
                                    </span>
                                    <span className={styles.optionGrid}>
                                        <span className={styles.optionColumn}>
                                            <img
                                                className={styles.optionImage}
                                                src={electricityOptInImage}
                                                alt={intl.formatMessage({ id: 'workpackForm.electricityOptIn.option.colourCoding.alt' })}
                                            />
                                        </span>
                                        <span className={styles.optionColumn}>
                                            <h4 className={styles.optionLegendTitle}>
                                                <FormattedMessage id='workpackForm.electricityOptIn.legend.title' />
                                            </h4>
                                            {COLOUR_CODING_ELECTRICITY_LEGEND_ITEMS.map(({ colour, id }) => (
                                                <div className={styles.optionLegendItem} key={id}>
                                                    <span
                                                        className={styles.optionSwatch}
                                                        style={{ backgroundColor: colour }}
                                                    />
                                                    <FormattedMessage id={`workpackForm.electricityOptIn.legend.${id}`} />
                                                </div>
                                            ))}
                                        </span>
                                    </span>
                                </OptIn.Option>
                            </div>
                        </OptIn>}
                    </div>
                </>
                : <>
                    <WorkpackSummary
                        enquiry={enquiry}
                        showSummary={!enquiry?.enquiryId}
                        change={setShowSummary}
                        changePurpose={handleChangePurpose}
                        capabilities={resolvedCapabilitiesRef.current}
                        aoi={aoi}
                        featuresByAssetType={featuresByAssetType}
                        onEditArea={handleEdit}
                    />
                    {/* Disclaimer Notice */}
                    <div className="govuk-inset-text govuk-!-margin-top-6">
                        <ul>
                            <li className="govuk-body">
                                {intl.formatMessage({ id: 'workpackSummary.disclaimer.line2' })}
                            </li>
                            <li className="govuk-body">
                                {intl.formatMessage({ id: 'workpackSummary.disclaimer.line3' })}
                            </li>
                            <li className="govuk-body">
                                {intl.formatMessage({ id: 'workpackSummary.disclaimer.line4' }, {
                                    linkHsg47: chunks => (
                                        <NavLink to={HSG47_PAGE} target="_blank" rel="noopener noreferrer">{chunks}</NavLink>
                                    )
                                })}
                            </li>
                        </ul>
                    </div>
                </>
            }
            <div className="govuk-grid-row">
                <div className="govuk-grid-column-two-thirds">
                    <div className="govuk-button-group govuk-!-margin-top-4">
                        {showSummary === false
                            ? <>
                                <Button
                                    variant="default"
                                    type="button"
                                    onPress={() => handleShowSummary()}
                                    isDisabled={!formik.values.title || !formik.values.geometry || !formik.values.workpackOrientation || !formik.values.workpackScale}
                                    data-testid='continueButton'
                                >
                                    {intl.formatMessage({ id: 'general.continue' })}
                                </Button>
                            </>
                            : <>
                                <Button
                                    variant="default"
                                    onPress={formik.submitForm}
                                    data-testid='submitButton'
                                >
                                    {intl.formatMessage({ id: 'workpackForm.submitButton.label' })}
                                </Button>
                            </>
                        }
                    </div>
                </div>
            </div>
        </form>
    );
};

WorkpackForm.propTypes = {
    ...defaultAriaProps,
    'data-testid': PropTypes.string,
    enquiry: PropTypes.object,
    onChange: PropTypes.func,
    setErrors: PropTypes.func,
    showSummary: PropTypes.bool,
    setShowSummary: PropTypes.func,
    handleEdit: PropTypes.func,
    handleChangePurpose: PropTypes.func
};

export default WorkpackForm;
